import {
  createFileRoute,
  redirect,
  useRouteContext,
} from '@tanstack/react-router'
import { useState } from 'react'
import { queryKeys, type DefaultLanguage } from '@/data/queries'
import { orgSettingsOptions } from '@/data/queryOptions'
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { mutationKeys, updateOrgSettings } from '@/data/mutations'
import { useIdTokenAndTenantId } from '@/hooks/useIdTokenAndTenantId'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { LanguagesIcon } from 'lucide-react'
import { languages } from '@/components/document-edit/TranslateDialog'

export const Route = createFileRoute(
  '/_authenticated/_settings/user/$userId/admin/language',
)({
  loader: async ({ context }) => {
    const { isOrgAdmin, authState, TENANT_ID } = context
    if (!TENANT_ID) {
      throw redirect({ to: '/select-tenant' })
    }
    if (!isOrgAdmin) {
      throw redirect({
        to: '/user/$userId',
        params: { userId: authState!.uid },
      })
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { authState } = useRouteContext({ from: '/_authenticated' })
  const { TENANT_ID, idToken } = useIdTokenAndTenantId()
  const queryClient = useQueryClient()

  const { data: defaultLanguageSetting } = useSuspenseQuery(
    orgSettingsOptions<DefaultLanguage>(
      TENANT_ID!,
      idToken,
      'default-language',
    ),
  )

  const [defaultLanguage, setDefaultLanguage] = useState(
    defaultLanguageSetting?.language || 'en',
  )

  const updateDefaultLanguageMutation = useMutation({
    mutationKey: mutationKeys.settings.update<DefaultLanguage>(
      TENANT_ID!,
      'default-language',
    ),
    mutationFn: (data: { language: string }) => {
      return updateOrgSettings<DefaultLanguage>({
        authState: authState!,
        tenantId: TENANT_ID!,
        name: 'default-language',
        data,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey:
          queryKeys.settings.byName<DefaultLanguage>('default-language'),
      })
    },
  })

  return (
    <div className="settings-container">
      {/* Default language settings */}
      <h1 className="text-2xl">Language Settings</h1>
      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-bold">
          Default Language{' '}
          <span className="text-copy-secondary text-xs">
            (Organization wide)
          </span>
        </h3>
        <p className="text-copy-secondary mb-2 text-sm">
          Customize the default language for the generated documents
        </p>
        <Select
          value={defaultLanguage}
          onValueChange={(value) => {
            setDefaultLanguage(value)
            updateDefaultLanguageMutation.mutate({
              language: value,
            })
          }}
        >
          <SelectTrigger className="w-64">
            <div className="flex items-center gap-2">
              <LanguagesIcon className="h-4 w-4" />
              <SelectValue />
            </div>
          </SelectTrigger>
          <SelectContent>
            {languages.map((language) => (
              <SelectItem key={language.value} value={language.value}>
                {language.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-copy-secondary mb-2 text-sm">
          This is the default language used to create documents.
        </p>
      </div>
    </div>
  )
}
